import { GOOGLE_AUTOMATION_STAGE_STATUSES } from './googleAutomationStageStatuses'
import { XPATHS, getVisibilityTargetXpath } from './googleProfilePhotoSelectors'
import * as googleUrls from './googleUrls'
import { GoogleSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { StageType, GoogleRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import SectionNotFoundException from '../exception/sectionNotFoundException'
import { throwIfTimeout, wait } from '../interruptiblePromises'
import {
  AutomationErrors,
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement, shouldUpdateSetting } from '../utils'

export { XPATHS, getVisibilityTargetXpath }

export type ProfilePictureVisibility =
  | 'anyone'
  | 'people_you_interact_with'
  | 'no_picture'

const VISIBILITY_DISPLAY: Record<string, string> = {
  anyone: 'Anyone',
  people_you_interact_with: 'People you interact with',
  no_picture: 'No profile picture',
}

// ── Utility functions ────────────────────────────────────────────────────────

/**
 * Polls iframe.contentDocument until a landmark XPath element exists,
 * returning the fresh document. Uses the main document.evaluate() with
 * the iframe doc as contextNode — matching the pattern used by
 * evaluateXpath() throughout the codebase. This is required because
 * Chrome content scripts run in an isolated world where the main
 * document's evaluate() is properly proxied, but calling evaluate()
 * directly on an iframe's contentDocument may not work. Races against
 * throwIfTimeout. Polls every 200ms using wait() which integrates with
 * the global interrupt system.
 */
async function waitForIframeContent(
  landmarkXpath: string,
  timeoutMs = 10_000,
): Promise<Document> {
  const poll = async (): Promise<Document> => {
    while (true) {
      const iframe = document.querySelector<HTMLIFrameElement>(
        'iframe[src*="/profile-picture"]',
      )
      const doc = iframe?.contentDocument
      if (doc) {
        try {
          const result = document.evaluate(
            landmarkXpath,
            doc,
            null,
            XPathResult.ANY_TYPE,
            null,
          )
          if (result.iterateNext()) return doc
        } catch {
          /* doc may be mid-navigation */
        }
      }
      await wait(200)
    }
  }
  return Promise.race([
    poll(),
    throwIfTimeout(
      `waitForIframeContent: timed out waiting for ${landmarkXpath}`,
      timeoutMs,
    ),
  ])
}

/**
 * Reads the <h1> in the iframe dialog to determine if the user has a profile
 * picture. "Change profile picture" means they have one; "Add profile picture"
 * means they don't.
 */
export async function hasProfilePicture(iframeDoc: Document): Promise<boolean> {
  const h1 = await getElement<HTMLHeadingElement>(iframeDoc, '//h1')
  return h1.textContent?.trim() !== 'Add profile picture'
}

/**
 * Opens the visibility settings screen by clicking the "Profile picture
 * visibility" button in the iframe dialog. Returns a fresh document
 * reference because clicking the button triggers iframe navigation, which
 * detaches the old document.
 *
 * ## Why this is complicated
 *
 * Google's profile-picture dialog runs inside an iframe whose DOM goes
 * through three separate readiness milestones, each of which can cause
 * `.click()` to silently fail:
 *
 * 1. **JS not loaded.** Google renders HTML before attaching `jsaction`
 *    click handlers. `getElement()` resolves on DOM presence, so the
 *    button can be found while `readyState` is still `"interactive"` or
 *    even `"loading"`. Clicking before handlers are wired is a no-op.
 *
 * 2. **DOM rebuild.** Google's JS tears down and rebuilds the DOM tree
 *    inside the iframe during initialization. A button reference captured
 *    before the rebuild becomes detached (`isConnected === false`), and
 *    `.click()` fires on an orphaned node with no effect.
 *
 * 3. **Button not yet visible.** Even after `readyState === "complete"`,
 *    the dialog's CSS open-animation may not have finished. The button
 *    exists in the DOM but has zero layout dimensions. Google's `jsaction`
 *    infrastructure ignores clicks on invisible elements.
 *
 * ### What didn't work
 *
 * - Waiting for `readyState === "complete"` alone fixed (1) but not (2)
 *   or (3).
 * - Re-querying the button after the readyState wait fixed (1)+(2) but
 *   not (3).
 * - Polling `getBoundingClientRect()` for non-zero dimensions always
 *   returned zeros from the content script's isolated world when reading
 *   iframe elements, causing an infinite wait.
 *
 * ### What works
 *
 * A readyState wait followed by a click-and-retry loop that re-queries
 * the button on each attempt. This is robust to all three failure modes:
 * each attempt gets a fresh DOM reference, clicks it, and waits up to 3s
 * for the radiogroup to appear. Five attempts with 500ms gaps gives the
 * dialog time to fully initialize across the slowest observed cases.
 */
export async function openVisibilitySettings(
  iframeDoc: Document,
): Promise<Document> {
  // Gate on button existence (iframe shell is loaded).
  await getElement<HTMLButtonElement>(iframeDoc, XPATHS.visibilityButton)

  // Wait for readyState "complete" (see JSDoc above for why).
  if (iframeDoc.readyState !== 'complete') {
    await Promise.race([
      new Promise<void>((resolve) => {
        const iframe = document.querySelector<HTMLIFrameElement>(
          'iframe[src*="/profile-picture"]',
        )
        if (iframe) {
          iframe.addEventListener('load', () => resolve(), { once: true })
        }
        const poll = setInterval(() => {
          if (iframeDoc.readyState === 'complete') {
            clearInterval(poll)
            resolve()
          }
        }, 100)
      }),
      wait(5000),
    ])
  }

  // Click-and-retry with fresh button reference each attempt (see JSDoc).
  const maxAttempts = 5
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const btn = await getElement<HTMLButtonElement>(
      iframeDoc,
      XPATHS.visibilityButton,
    )
    btn.click()
    try {
      return await waitForIframeContent(XPATHS.radiogroup, 3000)
    } catch (err) {
      if (attempt === maxAttempts - 1) throw err
      await wait(500)
    }
  }
  throw new Error('openVisibilitySettings: exhausted retries')
}

/**
 * Reads which radio button is currently checked in the visibility panel.
 * Handles both personal accounts (<input type="radio">) and Workspace
 * accounts (<div role="radio"> with aria-checked).
 */
export async function getSelectedVisibility(
  iframeDoc: Document,
): Promise<'anyone' | 'people_you_interact_with'> {
  const anyoneRadio = await getElement<HTMLElement>(
    iframeDoc,
    XPATHS.anyoneRadio,
  )
  const isChecked =
    anyoneRadio instanceof HTMLInputElement
      ? anyoneRadio.checked
      : anyoneRadio.getAttribute('aria-checked') === 'true'
  return isChecked ? 'anyone' : 'people_you_interact_with'
}

/**
 * Clicks the target visibility radio option. Handles both personal accounts
 * (click <label>) and Workspace accounts (click <div role="radio">).
 */
export async function selectVisibility(
  iframeDoc: Document,
  value: 'anyone' | 'people_you_interact_with',
): Promise<void> {
  const target = await getElement<HTMLElement>(
    iframeDoc,
    getVisibilityTargetXpath(value),
  )
  target.click()
}

/**
 * Clicks the Save button in the visibility panel.
 */
export async function clickSave(iframeDoc: Document): Promise<void> {
  const saveButton = await getElement<HTMLButtonElement>(
    iframeDoc,
    XPATHS.saveButton,
  )
  saveButton.click()
}

/**
 * Clicks the Cancel button in the visibility panel.
 */
export async function clickCancel(iframeDoc: Document): Promise<void> {
  const cancelButton = await getElement<HTMLButtonElement>(
    iframeDoc,
    XPATHS.cancelButton,
  )
  cancelButton.click()
}

/**
 * Opens the profile picture dialog by clicking the profile picture button
 * in the main document, then returns the iframe's contentDocument.
 */
export async function openProfilePictureDialog(): Promise<Document> {
  const profilePicButton = await getElement<HTMLButtonElement>(
    document,
    XPATHS.profilePicButton,
  )
  profilePicButton.click()
  return waitForIframeContent('//h1')
}

// ── Display functions ────────────────────────────────────────────────────────

export const getCurrentValueFn = (key: GoogleSettingKey) =>
  getCurrentValueFnFromKey(key, (value) => VISIBILITY_DISPLAY[value] ?? value)

export const getIsFulfilledFn = (key: GoogleSettingKey) =>
  getIsFulfilledFnFromKey(
    key,
    (value) => value === 'people_you_interact_with' || value === 'no_picture',
  )

export const getCantUndoFn = () => defaultCantUndoFn

export function getIsDisabledFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) => getErrorIsDisabledAndReasonFn(settingKey)(automationErrors).isDisabled
}

export function getIsDisabledReasonFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) =>
    getErrorIsDisabledAndReasonFn(settingKey)(automationErrors).isDisabledReason
}

// ── Stage definitions ────────────────────────────────────────────────────────

export const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'GATHER_GOOGLE_PROFILE_PHOTO_PROFILE_PHOTO_VISIBILITY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.profilePictureUrl.navigateForUser(googleAccountIndex)

      let iframeDoc = await openProfilePictureDialog()

      // Check if user has a profile picture
      if (!(await hasProfilePicture(iframeDoc))) {
        await userContext.updateStoredInitialValues(
          {
            [GoogleSettingKey.PROFILE_PHOTO_VISIBILITY]: 'no_picture',
          },
          GoogleRecommendationKey.GOOGLE_PROFILE_PHOTO,
        )
        return
      }

      // Navigate to visibility settings (re-acquires iframe doc after navigation)
      iframeDoc = await openVisibilitySettings(iframeDoc)
      const value = await getSelectedVisibility(iframeDoc)

      await userContext.updateStoredInitialValues(
        {
          [GoogleSettingKey.PROFILE_PHOTO_VISIBILITY]: value,
        },
        GoogleRecommendationKey.GOOGLE_PROFILE_PHOTO,
      )
    },
  },
]

export const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_PROFILE_PHOTO_PROFILE_PHOTO_VISIBILITY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex, initialValues, isDry, settingsSelected } =
        userContext

      if (
        !shouldUpdateSetting(
          settingsSelected,
          initialValues,
          GoogleSettingKey.PROFILE_PHOTO_VISIBILITY,
          getIsFulfilledFn,
        )
      ) {
        return
      }

      googleUrls.profilePictureUrl.navigateForUser(googleAccountIndex)

      let iframeDoc = await openProfilePictureDialog()

      // If no profile picture, nothing to do
      if (!(await hasProfilePicture(iframeDoc))) {
        return
      }

      // Navigate to visibility screen (re-acquires iframe doc after navigation)
      iframeDoc = await openVisibilitySettings(iframeDoc)
      await selectVisibility(iframeDoc, 'people_you_interact_with')

      if (isDry) {
        await clickCancel(iframeDoc)
      } else {
        await clickSave(iframeDoc)
      }
    },
  },
]

export const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_PROFILE_PHOTO_INTERSTITIAL',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.homeUrl.navigateForUser(googleAccountIndex)
    },
  },
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_PROFILE_PHOTO_PROFILE_PHOTO_VISIBILITY',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex, initialValues, isDry, settingsSelected } =
        userContext

      if (
        !shouldUpdateSetting(
          settingsSelected,
          initialValues,
          GoogleSettingKey.PROFILE_PHOTO_VISIBILITY,
          getIsFulfilledFn,
        )
      ) {
        return
      }

      googleUrls.profilePictureUrl.navigateForUser(googleAccountIndex)

      let iframeDoc = await openProfilePictureDialog()

      // If no profile picture, can't undo
      if (!(await hasProfilePicture(iframeDoc))) {
        throw new SectionNotFoundException(
          'Profile picture was removed since gather',
        )
      }

      const originalValue = initialValues?.[
        GoogleSettingKey.PROFILE_PHOTO_VISIBILITY
      ] as 'anyone' | 'people_you_interact_with'

      // Navigate to visibility screen (re-acquires iframe doc after navigation)
      iframeDoc = await openVisibilitySettings(iframeDoc)
      await selectVisibility(iframeDoc, originalValue)

      if (isDry) {
        await clickCancel(iframeDoc)
      } else {
        await clickSave(iframeDoc)
      }
    },
  },
]
